import { RouteNames } from '@/router/names';

export interface MiloSupportPageContext {
    label: string;
    suggestions: string[];
}

type PageContextKey =
    | 'dashboard'
    | 'invoices'
    | 'quotes'
    | 'customers'
    | 'customerGroups'
    | 'catalog'
    | 'projects'
    | 'timeEntries'
    | 'timeEntryReports'
    | 'recurringInvoices'
    | 'settings'
    | 'companySettings'
    | 'users'
    | 'branding'
    | 'profile'
    | 'apiTokens'
    | 'billing';

type PageContextDefinition = {
    labelKey: string;
    routes: string[];
    pathPrefixes: string[];
    titleKeys?: string[];
};

type Translate = (key: string, values?: Record<string, string>) => string;

const PAGE_CONTEXTS: Record<PageContextKey, PageContextDefinition> = {
    dashboard: {
        labelKey: 'navigation.dashboard',
        routes: [RouteNames.Home],
        pathPrefixes: ['/'],
        titleKeys: ['pages.home'],
    },
    invoices: {
        labelKey: 'navigation.invoices',
        routes: [
            RouteNames.Invoices,
            RouteNames.InvoiceCreate,
            RouteNames.InvoiceDetail,
            RouteNames.InvoiceView,
            RouteNames.InvoicePublicView,
            RouteNames.CreditNoteView,
            RouteNames.CreditNotePublicView,
        ],
        pathPrefixes: ['/invoices', '/credit-notes'],
        titleKeys: [
            'pages.invoices',
            'pages.createInvoice',
            'pages.viewInvoice',
            'pages.invoiceDetails',
            'pages.viewCreditNote',
        ],
    },
    quotes: {
        labelKey: 'navigation.quotes',
        routes: [
            RouteNames.Quotes,
            RouteNames.QuoteCreate,
            RouteNames.QuoteDetail,
            RouteNames.QuoteView,
            RouteNames.QuotePublicView,
        ],
        pathPrefixes: ['/quotes'],
        titleKeys: ['pages.quotes', 'pages.quoteDetails', 'pages.viewQuote'],
    },
    customers: {
        labelKey: 'navigation.customers',
        routes: [RouteNames.Customers, RouteNames.CustomerCreate, RouteNames.CustomerDetail, RouteNames.CustomerView],
        pathPrefixes: ['/customers'],
        titleKeys: ['pages.customers', 'customers.createCustomer', 'customers.customerDetails'],
    },
    customerGroups: {
        labelKey: 'navigation.customerGroups',
        routes: [RouteNames.CustomerGroups, RouteNames.CustomerGroupCreate, RouteNames.CustomerGroupDetail],
        pathPrefixes: ['/customer-groups'],
    },
    catalog: {
        labelKey: 'navigation.catalog',
        routes: [RouteNames.Catalog],
        pathPrefixes: ['/catalog'],
    },
    projects: {
        labelKey: 'navigation.projects',
        routes: [RouteNames.Projects, RouteNames.ProjectCreate, RouteNames.ProjectDetail],
        pathPrefixes: ['/projects'],
    },
    timeEntries: {
        labelKey: 'navigation.timeEntries',
        routes: [RouteNames.TimeEntries, RouteNames.TimeEntryCreate, RouteNames.TimeEntryDetail],
        pathPrefixes: ['/time-entries'],
    },
    timeEntryReports: {
        labelKey: 'navigation.timeEntryReports',
        routes: [RouteNames.TimeEntryReports],
        pathPrefixes: ['/time-entries/reports'],
    },
    recurringInvoices: {
        labelKey: 'navigation.recurringInvoices',
        routes: [
            RouteNames.RecurringInvoices,
            RouteNames.RecurringInvoiceCreate,
            RouteNames.RecurringInvoiceDetail,
            RouteNames.RecurringInvoiceEdit,
        ],
        pathPrefixes: ['/recurring-invoices'],
    },
    settings: {
        labelKey: 'navigation.settings',
        routes: [RouteNames.Settings, RouteNames.SettingsGeneral, RouteNames.SettingsPayments],
        pathPrefixes: ['/settings'],
    },
    companySettings: {
        labelKey: 'navigation.company_details',
        routes: [
            RouteNames.SettingsCompanies,
            RouteNames.SettingsCompany,
            RouteNames.SettingsCompanyCreate,
            RouteNames.SettingsCompanyDetail,
            RouteNames.SettingsCompanyView,
            RouteNames.Companies,
            RouteNames.CompanyCreate,
            RouteNames.CompanyDetail,
            RouteNames.CompanyView,
        ],
        pathPrefixes: ['/settings/company', '/companies'],
    },
    users: {
        labelKey: 'navigation.users',
        routes: [RouteNames.SettingsUsers, RouteNames.Users, RouteNames.UsersCreate, RouteNames.UsersDetail],
        pathPrefixes: ['/settings/users', '/users'],
    },
    branding: {
        labelKey: 'navigation.branding',
        routes: [RouteNames.SettingsBranding],
        pathPrefixes: ['/settings/branding'],
    },
    profile: {
        labelKey: 'navigation.profile',
        routes: [RouteNames.Profile, RouteNames.ChangePassword, RouteNames.SettingsSecurity],
        pathPrefixes: ['/profile', '/change-password'],
    },
    apiTokens: {
        labelKey: 'navigation.apiTokens',
        routes: [RouteNames.SettingsApiTokens],
        pathPrefixes: ['/settings/api-tokens'],
    },
    billing: {
        labelKey: 'navigation.billing',
        routes: [RouteNames.Billing, RouteNames.BillingReturn],
        pathPrefixes: ['/billing'],
    },
};

const PAGE_CONTEXT_BY_ROUTE = new Map(
    Object.entries(PAGE_CONTEXTS).flatMap(([key, definition]) =>
        definition.routes.map((routeName) => [routeName, key as PageContextKey] as const),
    ),
);

const PAGE_CONTEXT_BY_TITLE_KEY = new Map(
    Object.entries(PAGE_CONTEXTS).flatMap(([key, definition]) =>
        (definition.titleKeys ?? []).map((titleKey) => [titleKey, key as PageContextKey] as const),
    ),
);

const PAGE_CONTEXT_PATH_PREFIXES = Object.entries(PAGE_CONTEXTS)
    .flatMap(([key, definition]) =>
        definition.pathPrefixes.map((pathPrefix) => [pathPrefix, key as PageContextKey] as const),
    )
    .sort(([left], [right]) => right.length - left.length);

export function resolveMiloSupportPageContext(
    routeName: unknown,
    routePath: unknown,
    routeTitleKey: unknown,
    translate: Translate,
): MiloSupportPageContext {
    const routeContextKey = typeof routeName === 'string' ? PAGE_CONTEXT_BY_ROUTE.get(routeName) : undefined;
    const pathContextKey = typeof routePath === 'string' ? findPageContextForPath(routePath) : undefined;
    const titleContextKey =
        typeof routeTitleKey === 'string' ? PAGE_CONTEXT_BY_TITLE_KEY.get(routeTitleKey) : undefined;
    const contextKey = routeContextKey ?? pathContextKey ?? titleContextKey;

    if (contextKey) {
        const definition = PAGE_CONTEXTS[contextKey];
        return {
            label: translate(definition.labelKey),
            suggestions: suggestionKeys(contextKey).map((key) => translate(key)),
        };
    }

    const label = typeof routeTitleKey === 'string' ? translate(routeTitleKey) : translate('navigation.home');
    return {
        label,
        suggestions: suggestionKeys('generic').map((key) => translate(key, { page: label })),
    };
}

function findPageContextForPath(path: string): PageContextKey | undefined {
    return PAGE_CONTEXT_PATH_PREFIXES.find(([prefix]) =>
        prefix === '/' ? path === '/' : path === prefix || path.startsWith(`${prefix}/`),
    )?.[1];
}

function suggestionKeys(contextKey: PageContextKey | 'generic'): string[] {
    const prefix = `supportWidget.pageContext.${contextKey}`;
    return [`${prefix}.one`, `${prefix}.two`, `${prefix}.three`];
}
